import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { CountryMap, PartialCountryNameMap, ZipStatus } from '../components/Dropdown/CountriesData';
import { CHEGG } from '../constants/StorageConstants';

/**
 * returns a new Map data based on config values of mandatoryCountries and hiddenCountries
 * @returns {Map<string, CountryData>} Map of CountryData - if no cached data is found, default CountryMap is returned
 */

export var CachedCountriesData = function CachedCountriesData() {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(CHEGG.UA.COUNTRY_DATA)) {
    // need to parse JSON and create Map as JSON does not support to stringify Map data
    // https://stackoverflow.com/questions/29085197/how-do-you-json-stringify-an-es6-map
    // there is no wat the || statement in JSON.parse would execute due to the check above. But syntax requires having this check
    // disabling jest function check :)
    return new Map(JSON.parse(localStorage.getItem(CHEGG.UA.COUNTRY_DATA) || '{}'));
  } else {
    return CountryMap;
  }
};

/**
 * Takes a partial country name and returns a fully complete name if one exists in the map
 * @param {string} incompleteCountryName
 * @returns {string | undefined} full country name if found or undefined if not found
 */
export var getMappedFullCountryNameFromPartial = function getMappedFullCountryNameFromPartial(incompleteCountryName) {
  return PartialCountryNameMap.get(incompleteCountryName.toLowerCase());
};

/**
 * returns an array of country names from a localized country list
 * @param {Record<string, string>[]} localizedCountryList
 * @returns {string[]} list of country names sorted alphabetically
 */
export var getCountryNames = function getCountryNames(localizedCountryList) {
  return getCountryDataList(localizedCountryList).filter(Boolean).map(function (countryData) {
    return countryData.name;
  }).sort();
};

/**
 * generates a lookup object for unique IDs mapped to corresponding text values from a list of fields
 * @param {Record<string, string>[] | undefined} fields
 * @returns {object}  lookup object with unique IDs as keys and text values as values
 */
export var getUniqueIDLookUp = function getUniqueIDLookUp(fields) {
  var uniqueIdLookup = fields === null || fields === void 0 ? void 0 : fields.reduce(function (acc, field) {
    acc[field.uniqueId] = field.text;
    return acc;
  }, {});
  return {
    getValue: function getValue(key) {
      return uniqueIdLookup === null || uniqueIdLookup === void 0 ? void 0 : uniqueIdLookup[key];
    }
  };
};

/**
 * return a list of formatted country data objects with localized country names
 * @param {Record<string, string>[]} localizedCountryList
 * @returns {CountryData[]} list of country data objects
 */
export var getCountryDataList = function getCountryDataList(localizedCountryList) {
  var data = CachedCountriesData();
  var dataValues = Array.from(data.values());
  var countryNameObject = getUniqueIDLookUp(localizedCountryList);
  dataValues.filter(Boolean).map(function (item) {
    var countryName = countryNameObject.getValue(item.code);
    if (countryName) {
      item.name = countryName;
    }
    return item;
  });
  return dataValues;
};

/**
 * fetch the name of a country, given the country code
 * @param countryCode 2-letter country code (in uppercase)
 * @returns {string} country name or an empty string if no match is found
 */
export var getCountryName = function getCountryName(countryCode) {
  var matchedCountry = getCountryDataByCode(countryCode);
  return matchedCountry ? matchedCountry.name : '';
};

/**
 * get the country's ISO code, given the country name
 * @param {string} countryName
 * @param {Record<string, string>[]} localizedCountryList
 * @returns {string} 2-letter country code (in uppercase) or an empty string if no match is found
 */
export var getCountryCode = function getCountryCode(localizedCountryList, countryName) {
  var matchedCountry = getCountryDataList(localizedCountryList).find(
  // polyfill added under Utils/ArrayPolyfillsUtil
  function (countryData) {
    return countryData.name.toLowerCase() === countryName.toLowerCase();
  });
  return matchedCountry ? matchedCountry.code : '';
};

/**
 * gets a list of 2-character country ISO codes, in lowercase
 * @returns {string[]} list of 2-character country ISO codes, in lowercase
 */
export var getCountryCodeList = function getCountryCodeList() {
  var data = CachedCountriesData();
  //@ts-ignore
  return _toConsumableArray(data.keys()).map(function (code) {
    return code ? code.toString().toLowerCase() : '';
  });
};

/**
 * returns a country data object, given the country code
 * @param countryCode 2-letter country code (in uppercase)
 * @param {Record<string, string>[]} localizedCountryList
 * @returns {CountryData | undefined} country data object or undefined if no match is found
 */
export var getCountryDataByCode = function getCountryDataByCode(countryCode, localizedCountryList) {
  var countryListData = getCountryDataList(localizedCountryList).filter(Boolean).sort();
  var data = countryListData.find(function (item) {
    return item.code.toLowerCase() === countryCode.toLowerCase();
  });
  return data;
};

/**
 * returns a CountryData object, given the countryName
 * @param {string} countryName
 * @param {Record<string, string>[]} localizedCountryList
 * @returns {CountryData | undefined} country data object or undefined if no match is found
 */
export var getCountryDataByName = function getCountryDataByName(localizedCountryList, countryName) {
  return getCountryDataList(localizedCountryList).find(function (countryData) {
    return countryData.name === countryName;
  });
};

/**
 * return a new CountryData Map by setting mandatory and hidden zip status
 * @param mandatoryCountries array of 2-letter country code ISO (in uppercase)
 * @param hideCountries array of 2-letter country code ISO (in uppercase)
 * @returns {Map<string, CountryData>} Map of CountryData
 */
export var modifyCountriesData = function modifyCountriesData(mandatoryCountries
// hiddenCountries: string[]
) {
  var newCountriesDataMap = CachedCountriesData();
  mandatoryCountries.forEach(function (code) {
    var modifiedCountry = getCountryDataByCode(code);
    // Allow only for countries which are optional
    if (modifiedCountry && !modifiedCountry.zipStatus) {
      modifiedCountry.zipStatus = ZipStatus.mandatory;
      newCountriesDataMap = newCountriesDataMap.set(code, modifiedCountry);
    }
  });
  /* Commenting out this code if we need optional fields in future
    hiddenCountries.forEach(code => {
      const modifiedCountry = getCountryDataByCode(code);
      // Allow only for countries which are optional
      if (modifiedCountry && !modifiedCountry.zipStatus) {
        modifiedCountry.zipStatus = ZipStatus.hidden;
        newCountriesDataMap = newCountriesDataMap.set(code, modifiedCountry);
      }
    });
  */
  return newCountriesDataMap;
};


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/commerce-utils/dist/esm/utils/CountryUtil.js
// module id = 3502
// module chunks = 11